import { useState, useEffect, useRef } from 'react';

export default function Insight() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Load Google Fonts
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700;1,900&family=Dancing+Script:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      document.head.removeChild(link);
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-32 bg-gradient-to-br from-[#1a2f4f] via-[#2a4b7c] to-[#1e3a5f] relative overflow-hidden"
    >
      {/* Animated background orbs */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#06b6d4] rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#06b6d4] rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-[#0891b2] rounded-full blur-3xl animate-[float_12s_ease-in-out_infinite]" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-[#06b6d4] rounded-full opacity-30"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        {/* Icon with animation */}
        <div className={`inline-block mb-10 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
        }`}>
          <div className="w-24 h-24 mx-auto flex items-center justify-center bg-[#06b6d4]/20 rounded-2xl backdrop-blur-sm border border-[#06b6d4]/30 shadow-lg shadow-[#06b6d4]/20 group hover:scale-110 transition-transform duration-500">
            <i className="ri-lightbulb-flash-line text-5xl text-[#06b6d4] animate-pulse group-hover:animate-none group-hover:rotate-12 transition-transform duration-500"></i>
            <div className="absolute inset-0 bg-[#06b6d4]/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>
        
        {/* Main heading with animation */}
        <h2 
          className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <span className="block text-white italic mb-2 font-normal" style={{ fontStyle: 'italic', letterSpacing: '0.02em' }}>
            PHẦN MỀM CHỈ CÓ GIÁ TRỊ
          </span>
          <span className="block relative inline-block">
            <span 
              className="bg-gradient-to-r from-[#06b6d4] via-[#0891b2] to-[#06b6d4] bg-clip-text text-transparent animate-gradient font-normal"
              style={{ fontStyle: 'italic', letterSpacing: '0.02em' }}
            >
              KHI PHÙ HỢP NGHIỆP VỤ
            </span>
            {/* Animated underline */}
            <span className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#06b6d4] to-transparent transition-all duration-1000 delay-800 ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`} style={{ transformOrigin: 'center' }}></span>
          </span>
        </h2>

        {/* Decorative line with animation */}
        <div className={`w-32 h-1 bg-gradient-to-r from-transparent via-[#06b6d4] to-transparent mx-auto mt-8 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
        }`} style={{ transformOrigin: 'center' }}></div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(10px); }
          66% { transform: translateY(-10px) translateX(-10px); }
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
}